/**
 * DEFCON TERMINAL - Input Management
 * Handles keyboard events, command history, and tab completion
 */

/** Input event types */
export type InputEventType = 'submit' | 'change' | 'keypress' | 'history' | 'tab' | 'escape';

/** Input event data */
export interface InputEvent {
  type: InputEventType;
  value: string;
  key?: string;
  originalEvent?: KeyboardEvent;
}

/** Input event listener callback */
export type InputEventListener = (event: InputEvent) => void;

/** Tab completion callback - returns matching completions */
export type TabCompletionProvider = (partial: string) => string[];

/** Input handler configuration */
export interface InputHandlerConfig {
  historySize?: number;
  tabCompletionEnabled?: boolean;
  echoInput?: boolean;
  maxInputLength?: number;
}

/**
 * InputHandler class
 * Manages keyboard input, command history, and tab completion
 */
export class InputHandler {
  private inputElement: HTMLInputElement;
  private config: Required<InputHandlerConfig>;

  private eventListeners: Map<InputEventType, Set<InputEventListener>>;
  private commandHistory: string[];
  private historyIndex: number;
  private currentInput: string;

  private tabCompletionProviders: TabCompletionProvider[];
  private tabCompletionIndex: number;
  private tabCompletionCandidates: string[];
  private lastTabValue: string;

  private isEnabled: boolean;

  constructor(inputElement: HTMLInputElement, config: InputHandlerConfig = {}) {
    this.inputElement = inputElement;
    this.config = {
      historySize: config.historySize ?? 50,
      tabCompletionEnabled: config.tabCompletionEnabled ?? true,
      echoInput: config.echoInput ?? true,
      maxInputLength: config.maxInputLength ?? 256,
    };

    this.eventListeners = new Map();
    this.commandHistory = [];
    this.historyIndex = -1;
    this.currentInput = '';

    this.tabCompletionProviders = [];
    this.tabCompletionIndex = -1;
    this.tabCompletionCandidates = [];
    this.lastTabValue = '';

    this.isEnabled = true;

    this.setupEventListeners();
  }

  // Bound handlers for cleanup
  private boundHandleKeyDown!: (e: KeyboardEvent) => void;
  private boundHandleInput!: (e: Event) => void;

  /**
   * Setup keyboard event listeners
   */
  private setupEventListeners(): void {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleInput = this.handleInput.bind(this);
    this.inputElement.addEventListener('keydown', this.boundHandleKeyDown);
    this.inputElement.addEventListener('input', this.boundHandleInput);
  }

  /**
   * Handle keydown events
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.isEnabled) {
      e.preventDefault();
      return;
    }

    switch (e.key) {
      case 'Enter':
        this.handleEnter(e);
        break;

      case 'ArrowUp':
        this.handleArrowUp(e);
        break;

      case 'ArrowDown':
        this.handleArrowDown(e);
        break;

      case 'Tab':
        this.handleTab(e);
        break;

      case 'Escape':
        this.handleEscape(e);
        break;

      case 'Backspace':
        this.resetTabCompletion();
        this.emit('keypress', e.key, e);
        break;

      default:
        this.resetTabCompletion();
        this.emit('keypress', e.key, e);
        break;
    }
  }

  /**
   * Handle input events
   */
  private handleInput(e: Event): void {
    const value = this.inputElement.value;

    // Enforce max length
    if (value.length > this.config.maxInputLength) {
      this.inputElement.value = value.slice(0, this.config.maxInputLength);
    }

    this.emit('change', this.inputElement.value);
  }

  /**
   * Handle Enter key - submit command
   */
  private handleEnter(e: KeyboardEvent): void {
    e.preventDefault();

    const value = this.inputElement.value.trim();

    if (value.length > 0) {
      // Add to history
      this.addToHistory(value);

      // Emit submit event
      this.emit('submit', value, e);
    }

    // Reset state
    this.inputElement.value = '';
    this.historyIndex = -1;
    this.currentInput = '';
    this.resetTabCompletion();
  }

  /**
   * Handle Arrow Up - navigate history backwards
   */
  private handleArrowUp(e: KeyboardEvent): void {
    e.preventDefault();

    if (this.commandHistory.length === 0) return;

    // Save current input if starting navigation
    if (this.historyIndex === -1) {
      this.currentInput = this.inputElement.value;
    }

    // Navigate backwards in history
    if (this.historyIndex < this.commandHistory.length - 1) {
      this.historyIndex++;
      this.inputElement.value = this.commandHistory[this.historyIndex];
      this.emit('history', this.inputElement.value, e);
    }

    // Move cursor to end
    this.moveCursorToEnd();
  }

  /**
   * Handle Arrow Down - navigate history forwards
   */
  private handleArrowDown(e: KeyboardEvent): void {
    e.preventDefault();

    if (this.historyIndex === -1) return;

    // Navigate forwards in history
    if (this.historyIndex > 0) {
      this.historyIndex--;
      this.inputElement.value = this.commandHistory[this.historyIndex];
    } else {
      // Back to current input
      this.historyIndex = -1;
      this.inputElement.value = this.currentInput;
    }

    this.emit('history', this.inputElement.value, e);
    this.moveCursorToEnd();
  }

  /**
   * Handle Tab key - autocomplete
   */
  private handleTab(e: KeyboardEvent): void {
    e.preventDefault();

    if (!this.config.tabCompletionEnabled) return;

    const currentValue = this.inputElement.value;

    // Check if we're continuing a tab completion cycle
    if (currentValue !== this.lastTabValue || this.tabCompletionCandidates.length === 0) {
      // Start new completion
      this.tabCompletionCandidates = this.getCompletions(currentValue);
      this.tabCompletionIndex = -1;
    }

    if (this.tabCompletionCandidates.length === 0) return;

    // Cycle through candidates
    this.tabCompletionIndex = (this.tabCompletionIndex + 1) % this.tabCompletionCandidates.length;
    const completion = this.tabCompletionCandidates[this.tabCompletionIndex];

    this.inputElement.value = completion;
    this.lastTabValue = completion;

    this.emit('tab', completion, e);
    this.moveCursorToEnd();
  }

  /**
   * Handle Escape key - clear input
   */
  private handleEscape(e: KeyboardEvent): void {
    e.preventDefault();

    this.inputElement.value = '';
    this.historyIndex = -1;
    this.currentInput = '';
    this.resetTabCompletion();

    this.emit('escape', '', e);
  }

  /**
   * Add command to history
   */
  private addToHistory(command: string): void {
    // Don't add duplicates of the last command
    if (this.commandHistory.length > 0 && this.commandHistory[0] === command) {
      return;
    }

    // Add to front of history
    this.commandHistory.unshift(command);

    // Trim to max size
    if (this.commandHistory.length > this.config.historySize) {
      this.commandHistory = this.commandHistory.slice(0, this.config.historySize);
    }
  }

  /**
   * Get tab completions from all providers
   */
  private getCompletions(partial: string): string[] {
    const completions: Set<string> = new Set();

    for (const provider of this.tabCompletionProviders) {
      const results = provider(partial);
      results.forEach(r => completions.add(r));
    }

    return Array.from(completions).sort();
  }

  /**
   * Reset tab completion state
   */
  private resetTabCompletion(): void {
    this.tabCompletionIndex = -1;
    this.tabCompletionCandidates = [];
    this.lastTabValue = '';
  }

  /**
   * Move cursor to end of input
   */
  private moveCursorToEnd(): void {
    const length = this.inputElement.value.length;
    this.inputElement.setSelectionRange(length, length);
  }

  /**
   * Add event listener
   */
  public on(event: InputEventType, listener: InputEventListener): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(listener);
  }

  /**
   * Remove event listener
   */
  public off(event: InputEventType, listener: InputEventListener): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit(type: InputEventType, value: string, originalEvent?: KeyboardEvent): void {
    const event: InputEvent = {
      type,
      value,
      key: originalEvent?.key,
      originalEvent,
    };

    const listeners = this.eventListeners.get(type);
    if (listeners) {
      listeners.forEach(listener => listener(event));
    }
  }

  /**
   * Add tab completion provider
   */
  public addCompletionProvider(provider: TabCompletionProvider): void {
    this.tabCompletionProviders.push(provider);
  }

  /**
   * Remove tab completion provider
   */
  public removeCompletionProvider(provider: TabCompletionProvider): void {
    const index = this.tabCompletionProviders.indexOf(provider);
    if (index > -1) {
      this.tabCompletionProviders.splice(index, 1);
    }
  }

  /**
   * Set known commands for basic tab completion
   */
  public setKnownCommands(commands: string[]): void {
    // Remove any existing basic command provider
    this.tabCompletionProviders = this.tabCompletionProviders.filter(
      p => !(p as unknown as { __isBasicProvider?: boolean }).__isBasicProvider
    );

    // Add new basic provider
    const provider: TabCompletionProvider & { __isBasicProvider?: boolean } = (partial: string) => {
      const lower = partial.toLowerCase();
      return commands.filter(cmd => cmd.toLowerCase().startsWith(lower));
    };
    provider.__isBasicProvider = true;

    this.tabCompletionProviders.push(provider);
  }

  /**
   * Enable/disable input handling
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.inputElement.disabled = !enabled;
  }

  /**
   * Check if input is enabled
   */
  public isInputEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Get command history
   */
  public getHistory(): readonly string[] {
    return [...this.commandHistory];
  }

  /**
   * Clear command history
   */
  public clearHistory(): void {
    this.commandHistory = [];
    this.historyIndex = -1;
  }

  /**
   * Set command history
   */
  public setHistory(history: string[]): void {
    this.commandHistory = history.slice(0, this.config.historySize);
    this.historyIndex = -1;
  }

  /**
   * Get current input value
   */
  public getValue(): string {
    return this.inputElement.value;
  }

  /**
   * Set input value
   */
  public setValue(value: string): void {
    this.inputElement.value = value.slice(0, this.config.maxInputLength);
    this.moveCursorToEnd();
  }

  /**
   * Focus input element
   */
  public focus(): void {
    this.inputElement.focus();
  }

  /**
   * Blur input element
   */
  public blur(): void {
    this.inputElement.blur();
  }

  /**
   * Clear input
   */
  public clear(): void {
    this.inputElement.value = '';
    this.resetTabCompletion();
  }

  /**
   * Get configuration
   */
  public getConfig(): Readonly<Required<InputHandlerConfig>> {
    return { ...this.config };
  }

  /**
   * Dispose of the input handler and clean up resources
   */
  public dispose(): void {
    // Remove DOM event listeners
    this.inputElement.removeEventListener('keydown', this.boundHandleKeyDown);
    this.inputElement.removeEventListener('input', this.boundHandleInput);

    // Clear all custom event listeners
    this.eventListeners.clear();

    // Clear history and completion state
    this.commandHistory = [];
    this.tabCompletionProviders = [];
    this.tabCompletionCandidates = [];

    this.isEnabled = false;
  }
}

export default InputHandler;
